/**
 * Sentence-pipelined TTS for streaming companion answers.
 *
 * Waiting for the full answer before speaking wastes the stream: a 350-word
 * answer decodes in ~5s but takes ~2 minutes to SAY, so the only latency
 * that matters is time-to-first-spoken-word. This module segments the
 * incoming answer_delta text at sentence boundaries, strips residual
 * markdown (the voice contract forbids it, but models slip), and feeds
 * sentences through an audio queue that prefetches the next clip while the
 * current one plays. First audio lands right after the first sentence
 * finishes decoding (~2.5s), and playback then always outruns synthesis.
 *
 * Transport and playback are injected so the core is hermetic:
 *   synthesize(text) -> audio handle     (e.g. POST /v1/companion/tts)
 *   play(audio, signal) -> resolves when done or rejects on abort
 *
 * Barge-in: cancel() aborts the current playback, discards the queue, and
 * aborts in-flight synthesis. This slots into the duplex design's
 * duck-then-clear order (the caller ducks the mic path; we clear).
 */

import { stripSpokenMarkdown } from '@edge/edge-ai'

// ── spoken-text cleanup ──────────────────────────────────────────────────────

// The cleaner lives in @edge/edge-ai so the clip fallback speaks the same way.
export { stripSpokenMarkdown }

// ── incremental sentence segmentation ────────────────────────────────────────

// A boundary is . ! or ? (optionally followed by a closing quote/paren) and
// then whitespace. Deliberately NOT a boundary: decimals (21.3), dotted
// tickers (BRK.B), and common abbreviations.
const ABBREVIATIONS = /(?:\b(?:vs|inc|corp|co|ltd|dr|mr|ms|approx|est|dept|no)\.\s*)$/i

function boundaryIndex(buffer: string): number | null {
  const re = /[.!?]["')\]]?\s/g
  let match: RegExpExecArray | null
  while ((match = re.exec(buffer)) !== null) {
    const end = match.index + 1
    const before = buffer.slice(0, end)
    // Decimal or dotted-symbol: digit/letter directly on both sides of a '.'.
    const prev = buffer[match.index - 1] ?? ''
    const next = buffer[match.index + 1] ?? ''
    if (buffer[match.index] === '.' && /[0-9A-Za-z]/.test(prev) && /[0-9A-Za-z]/.test(next)) {
      continue
    }
    if (ABBREVIATIONS.test(before)) continue
    return end + (match[0].length - 1 - 1) // include trailing quote/paren, not the whitespace
  }
  return null
}

/** Accumulates streamed deltas and emits complete sentences. */
/**
 * Drops text inside ``` fences from a stream (2026-10-02: answers can carry
 * ```chart blocks, which are drawn, never read aloud). Holds back trailing
 * backticks so a fence split across deltas is still recognised.
 */
export class FenceFilter {
  private inFence = false
  private pending = ''

  feed(delta: string): string {
    let text = this.pending + delta
    this.pending = ''
    const tail = /`{1,2}$/.exec(text)
    if (tail) {
      this.pending = tail[0]
      text = text.slice(0, -tail[0].length)
    }
    let out = ''
    let i = 0
    for (;;) {
      const j = text.indexOf('```', i)
      if (j < 0) {
        if (!this.inFence) out += text.slice(i)
        break
      }
      if (!this.inFence) out += text.slice(i, j)
      this.inFence = !this.inFence
      i = j + 3
    }
    return out
  }

  /** End of stream: release held-back backticks unless inside a fence. */
  end(): string {
    const rest = this.inFence ? '' : this.pending
    this.pending = ''
    return rest
  }
}

export class SentenceSegmenter {
  private buffer = ''
  private readonly fence = new FenceFilter()

  /** Push a delta; returns any sentences completed by it. */
  push(delta: string): string[] {
    this.buffer += this.fence.feed(delta)
    const out: string[] = []
    let idx: number | null
    while ((idx = boundaryIndex(this.buffer)) !== null) {
      const sentence = this.buffer.slice(0, idx).trim()
      this.buffer = this.buffer.slice(idx)
      if (sentence) out.push(sentence)
    }
    return out
  }

  /** End of stream: whatever remains is the final sentence. */
  flush(): string[] {
    const rest = (this.buffer + this.fence.end()).trim()
    this.buffer = ''
    return rest ? [rest] : []
  }
}

// ── the pipeline ─────────────────────────────────────────────────────────────

export interface SentenceTtsDeps<A> {
  /** Synthesize one sentence. Honor the signal for barge-in aborts. */
  synthesize(text: string, signal: AbortSignal): Promise<A>
  /** Play one clip to completion. Honor the signal for barge-in aborts. */
  play(audio: A, signal: AbortSignal): Promise<void>
  /** How many clips to synthesize ahead of playback (default 2). */
  prefetch?: number
  onError?(sentence: string, error: unknown): void
}

interface QueueEntry<A> {
  sentence: string
  audio: Promise<A>
}

/**
 * Feed sentences in as they complete; audio plays in order, with the next
 * clips synthesizing while the current one plays.
 */
export class SentenceTtsPipeline<A> {
  private queue: QueueEntry<A>[] = []
  private playing = false
  private done = false
  private cancelled = false
  private abort = new AbortController()
  private inFlight = 0
  private resolveFinished: (() => void) | null = null
  /** Resolves when every queued sentence has finished playing. */
  readonly finished: Promise<void>

  constructor(private deps: SentenceTtsDeps<A>) {
    this.finished = new Promise((resolve) => {
      this.resolveFinished = resolve
    })
  }

  private get prefetchLimit(): number {
    return this.deps.prefetch ?? 2
  }

  /** Enqueue a sentence (markdown-stripped here as the safety net). */
  speak(sentence: string): void {
    if (this.cancelled) return
    const text = stripSpokenMarkdown(sentence).trim()
    if (!text) return
    // Lazy synthesis: start immediately only while within the prefetch
    // window; later entries kick off as the queue drains.
    const entry: QueueEntry<A> = {
      sentence: text,
      audio:
        this.inFlight < this.prefetchLimit
          ? this.startSynthesis(text)
          : (null as unknown as Promise<A>),
    }
    this.queue.push(entry)
    void this.pump()
  }

  /** No more sentences will arrive; `finished` resolves after the last clip. */
  end(): void {
    this.done = true
    void this.pump()
  }

  /** Barge-in: stop the current clip, drop the queue, abort synthesis. */
  cancel(): void {
    this.cancelled = true
    this.queue = []
    this.abort.abort()
    this.resolveFinished?.()
  }

  private startSynthesis(text: string): Promise<A> {
    this.inFlight += 1
    const p = this.deps.synthesize(text, this.abort.signal)
    // Swallow on this side chain so the bookkeeping never surfaces as an
    // unhandled rejection; the queue awaits the ORIGINAL promise and handles
    // its rejection there.
    void p
      .catch(() => {})
      .finally(() => {
        this.inFlight -= 1
      })
    return p
  }

  private async pump(): Promise<void> {
    if (this.playing || this.cancelled) return
    this.playing = true
    try {
      while (this.queue.length > 0) {
        const entry = this.queue[0]!
        if (entry.audio === null) entry.audio = this.startSynthesis(entry.sentence)
        // Keep the prefetch window warm behind the head.
        for (let i = 1; i < this.queue.length && i < this.prefetchLimit; i++) {
          const next = this.queue[i]!
          if (next.audio === null) next.audio = this.startSynthesis(next.sentence)
        }
        let audio: A
        try {
          audio = await entry.audio
        } catch (e) {
          if (!this.cancelled) this.deps.onError?.(entry.sentence, e)
          this.queue.shift()
          continue
        }
        if (this.cancelled) return
        try {
          await this.deps.play(audio, this.abort.signal)
        } catch (e) {
          if (this.cancelled) return
          this.deps.onError?.(entry.sentence, e)
        }
        this.queue.shift()
      }
    } finally {
      this.playing = false
    }
    if (this.queue.length > 0) return void this.pump()
    if (this.done || this.cancelled) this.resolveFinished?.()
  }
}

/**
 * Convenience: wire a streaming answer into speech.
 * Call `onDelta` per answer_delta, `onDone` at stream end; `cancel` for
 * barge-in. Returns the pipeline for `finished` awaiting.
 */
export function speakStream<A>(deps: SentenceTtsDeps<A>): {
  onDelta(text: string): void
  onDone(): void
  cancel(): void
  pipeline: SentenceTtsPipeline<A>
} {
  const segmenter = new SentenceSegmenter()
  const pipeline = new SentenceTtsPipeline(deps)
  return {
    onDelta(text) {
      for (const sentence of segmenter.push(text)) pipeline.speak(sentence)
    },
    onDone() {
      for (const sentence of segmenter.flush()) pipeline.speak(sentence)
      pipeline.end()
    },
    cancel() {
      pipeline.cancel()
    },
    pipeline,
  }
}
